import { DeleteTwoTone, EditTwoTone, EyeOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Col, Dropdown, Menu, Popconfirm, Row, Statistic } from "antd";
import ListTable from "common/ListTable";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DELETE_EXPORT_DETAILS,
  GET_EXPORT_DETAILS_LIST,
  GET_EXPORT_DETAILS_SUMMARY,
} from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import {
  EXPORT_DETAILS_CREATE_PERMISSION,
  EXPORT_DETAILS_DELETE_PERMISSION,
  EXPORT_DETAILS_EDIT_PERMISSION,
  EXPORT_DETAILS_VIEW_PERMISSION,
} from "../../../routes/permissions";
import {
  cglFormatDate,
  formatNumberWithCommas,
  hasPermission,
} from "../../../utils/functions";
import { isMobileView } from "../../Common/AppHelper";
import BodyHeader from "../../Common/BodyHeader";
import CustomFilter from "../../Common/CustomFilter";
import Header from "../../Common/Header";

export default function ExportDetails(props) {
  // Props
  const { width } = props;

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  //   States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [exportDetailsList, setExportDetailsList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState();
  const [searchValues, setSearchValues] = useState();
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const lastFilterRef = useRef({});

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const fetchSummaryData = useCallback(async (salesContract) => {
    console.log("Fetching summary for sales contract:", salesContract);
    if (!salesContract) {
      setSummaryData(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const response = await getData(GET_EXPORT_DETAILS_SUMMARY, false, {
        sales_contract_id: salesContract,
      });

      if (response && response?.status === 200) {
        setSummaryData(response.data.data);
      } else {
        alertPop("error", "Failed to fetch summary data");
        setSummaryData(null);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      alertPop("error", "Error fetching summary data");
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: ["invoice_number"],
      key: "invoice_number",
      width: "20%",
      render: (value, record) => (
        <Link to={`/export/edit-export-details/${record.id}`} target="_blank">
          {value}
        </Link>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: ["invoice_date"],
      key: "invoice_date",
      width: "16%",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Invoice Value",
      dataIndex: ["invoice_value"],
      key: "invoice_value",
      render: (value) => formatNumberWithCommas(value),
      width: "16%",
    },
    {
      title: "Invoice Quantity",
      dataIndex: ["total_quantity"],
      key: "total_quantity",
      render: (value) => formatNumberWithCommas(value),
      width: "16%",
    },
    {
      title: "OnBoard Date",
      dataIndex: ["on_board_date"],
      key: "on_board_date",
      width: "16%",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Action",
      key: "action",
      width: "16%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          removeExportDetails(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([
                      EXPORT_DETAILS_DELETE_PERMISSION,
                    ]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openForEdit(record?.id);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([EXPORT_DETAILS_EDIT_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EyeOutlined />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openForView(record?.id);
                          }}
                        >
                          View
                        </span>
                      </>
                    ),
                    key: "3",
                    disabled: !hasPermission([EXPORT_DETAILS_VIEW_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const getAllExportDetails = useCallback(
    async (passedObject) => {
      setLoading(true); // Set loading to true at the start
      let filter = passedObject || {};
      lastFilterRef.current = filter; // persist the filter
      filter = {
        per_page: filter?.per_page || 10,
        page: currentPage,
        ...filter,
      };

      await fetchSummaryData(filter.sales_contract_id);

      let response = await getData(GET_EXPORT_DETAILS_LIST, false, filter);

      if (response && response?.status === 200) {
        let masterData = response?.data;
        setExportDetailsList(masterData?.data?.data);
        setTotalData(masterData?.data?.total);
        setRangeData(masterData?.data?.per_page);
      } else {
        alertPop(
          "error",
          response?.messages ? response?.messages : "Something Went Wrong!",
        );
      }
      setLoading(false);
    },
    [currentPage, fetchSummaryData],
  ); // currentPage as a dependency

  const removeExportDetails = async (value) => {
    const response = await deleteData(`${DELETE_EXPORT_DETAILS}/${value}`);

    if (response && response?.code === 200) {
      alertPop(
        "success",
        response?.messages ? response?.messages : "Deleted Successfully!",
      );
      getAllExportDetails();
    } else {
      alertPop(
        "error",
        response?.messages ? response?.messages : "Something Went Wrong!",
      );
    }
  };

  const openForEdit = (id) => {
    navigate(`/export/edit-export-details/${id}`);
  };

  const openForView = (id) => {
    navigate(`/export/view-export-details/${id}`);
  };

  const paginate = (page) => {
    setCurrentPage(page);
    getAllExportDetails(lastFilterRef.current || {});
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={exportDetailsList}
        fetchTableData={getAllExportDetails}
        filterName="export_details"
      />
    ),
    [showFilter, exportDetailsList, getAllExportDetails],
  );

  // Load initial data when component mounts
  useEffect(() => {
    setLoading(true);
    getAllExportDetails();
  }, [getAllExportDetails]);

  // Handle search values changes
  useEffect(() => {
    if (searchValues) {
      setLoading(true);
      getAllExportDetails(searchValues);
    }
  }, [searchValues, getAllExportDetails]);

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title="Export Details"
            subTitle=""
            actions={[
              <Button
                type={showFilter ? "danger" : "primary"}
                size="small"
                onClick={handleFilter}
                style={{ float: "right" }}
              >
                {`${showFilter ? "Close" : "Open"} Filter Option`}
              </Button>,
              <Button
                key="3"
                size="small"
                type="primary"
                disabled={!hasPermission([EXPORT_DETAILS_CREATE_PERMISSION])}
              >
                <Link to="/export/add-export-details">
                  {hasPermission([EXPORT_DETAILS_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"}
                </Link>
              </Button>,
            ]}
          ></Header>
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          <BodyHeader searchFilter={(values) => {}} />
          {memoizedCustomFilter}

          {/* Summary Data Section */}
          {summaryData && (
            <Card
              style={{ marginBottom: 16 }}
              loading={summaryLoading}
              title="Export Summary"
              bordered={false}
            >
              <Row gutter={16}>
                <Col span={4}>
                  <Statistic
                    title="Total Invoice Value"
                    value={formatNumberWithCommas(
                      summaryData.total_invoice_value || 0,
                    )}
                    precision={2}
                    suffix="USD"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="Total Shipment Quantity"
                    value={summaryData.total_shipment_qty || 0}
                    precision={0}
                    suffix="Pcs"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="Total Remaining Quantity"
                    value={summaryData.total_remain_qty || 0}
                    precision={0}
                    suffix="Pcs"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="SC Value"
                    value={summaryData.sc_value || 0}
                    precision={0}
                    suffix="USD"
                  />
                </Col>
                <Col span={4}>
                  <Statistic
                    title="SC Order QTY"
                    value={summaryData.sc_order_qty || 0}
                    precision={0}
                    suffix="Pcs"
                  />
                </Col>
              </Row>
            </Card>
          )}

          <ListTable
            loading={loading}
            dataSource={exportDetailsList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
}
